import { useEffect, useRef, useState } from "react";
import { searchCities } from "../../../api/cities";
import type { CityDto } from "../../../types/city";
import { FieldLabel, TextInput } from "../_components/AdminShell";

type CityIdsPickerProps = {
  cityIds: number[];
  cityLabels: Record<string, string>;
  onChange: (next: {
    cityIds: number[];
    cityLabels: Record<string, string>;
  }) => void;
};

export function CityIdsPicker({
  cityIds,
  cityLabels,
  onChange,
}: CityIdsPickerProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CityDto[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const selectedSet = new Set(cityIds);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const cities = await searchCities(trimmed);
        if (requestIdRef.current !== requestId) return;
        setResults(
          cities.slice().sort((a, b) => a.name.localeCompare(b.name, "ru")),
        );
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        setResults([]);
        setError(
          err instanceof Error ? err.message : "Не удалось найти города",
        );
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(timer);
  }, [query]);

  const addCity = (city: CityDto) => {
    if (selectedSet.has(city.id)) return;
    onChange({
      cityIds: [...cityIds, city.id],
      cityLabels: { ...cityLabels, [String(city.id)]: city.name },
    });
    setQuery("");
    setResults([]);
  };

  const removeCity = (id: number) => {
    const nextIds = cityIds.filter((item) => item !== id);
    const nextLabels = { ...cityLabels };
    delete nextLabels[String(id)];
    onChange({ cityIds: nextIds, cityLabels: nextLabels });
  };

  return (
    <div className="space-y-2">
      <FieldLabel size="compact">Города (whitelist)</FieldLabel>

      {cityIds.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {cityIds.map((id) => {
            const key = String(id);
            const label = cityLabels[key] || `id ${id}`;
            return (
              <span
                key={key}
                className="inline-flex items-center gap-1 rounded-md bg-brand-100 px-2 py-1 font-onest text-[11px] font-medium text-brand-700"
              >
                {label}
                <button
                  type="button"
                  className="text-brand-500 hover:text-brand-900"
                  aria-label={`Убрать ${label}`}
                  onClick={() => removeCity(id)}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      ) : (
        <p className="font-onest text-[11px] text-slate-500">
          Пустой список — на форме доступны все города. Добавьте города, чтобы
          ограничить выбор.
        </p>
      )}

      <TextInput
        size="compact"
        value={query}
        placeholder="Начните вводить название, например Ростов"
        onChange={setQuery}
      />

      {isLoading ? (
        <p className="font-onest text-[11px] text-slate-500">Поиск…</p>
      ) : null}
      {error ? (
        <p className="font-onest text-[11px] text-red-600" role="alert">
          {error}
        </p>
      ) : null}

      {!isLoading &&
      query.trim().length >= 2 &&
      results.length === 0 &&
      !error ? (
        <p className="font-onest text-[11px] text-slate-500">
          Ничего не найдено
        </p>
      ) : null}

      {results.length > 0 ? (
        <ul className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white">
          {results.map((city) => {
            const active = selectedSet.has(city.id);
            return (
              <li key={city.id}>
                <button
                  type="button"
                  disabled={active}
                  onClick={() => addCity(city)}
                  className={[
                    "flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left font-onest text-xs transition-colors",
                    active
                      ? "cursor-default bg-slate-50 text-slate-400"
                      : "text-slate-700 hover:bg-slate-50",
                  ].join(" ")}
                >
                  <span className="truncate">{city.name}</span>
                  <span className="shrink-0 font-mono text-[10px] text-slate-400">
                    {active ? "добавлен" : city.id}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
